/**
 * Dashboard (mockup v7): KPI cards with sparklines, donut, documents by type, gauges, columns, queue, activity.
 * 2026-10-23 (Bill): "Documents by company" stacked by 23 types was unreadable; replaced by horizontal bars by type
 * (top 7 + Other). Reviews show overdue + 3 months; Reviews and Your queue share row 2; Recent activity spans row 3.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type Dashboard } from "../lib/api";
import { useStore } from "../lib/store";
import { CH, TYPE_COLORS, TYPE_LABELS, coName } from "../lib/brand";
import { fmtDT } from "../lib/format";
import { Columns, Donut, Gauge, HBars, Spark, type Seg } from "../components/Charts";
import { Empty, Loading, TypeBadge } from "../components/bits";

const AREA_COLORS = ["#DC6D27", "#3B82C4", "#1F9E8F", "#7C5CC4", "#E0A31A", "#D9486B"];
const VERB: Record<string, string> = {
  "document.create": "started a draft of",
  "version.submit": "submitted",
  "version.changes_requested": "requested changes on",
  "version.approve": "approved",
};

export function DashboardPage() {
  const { me, p, ws, docs } = useStore();
  const nav = useNavigate();
  const [data, setData] = useState<Dashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setErr(null);
    api.dashboard(ws).then((d) => live && setData(d), (e: Error) => live && setErr(e.message));
    return () => { live = false; };
  }, [ws, docs]);

  const hr = new Date().getHours();
  const greet = hr < 12 ? "Good morning" : hr < 17 ? "Good afternoon" : "Good evening";
  const wsLabel = ws === "ALL" ? "all your companies" : coName(ws);
  const first = me.user.display_name.split(" ")[0] ?? me.user.display_name;
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  if (err) return <div className="card"><Empty title="The dashboard didn't load">{err}</Empty></div>;
  if (!data) return <Loading label="Loading your dashboard…" />;

  const c = data.counts;
  const libTab = (tab: string) => nav(`/library?tab=${tab}`);
  const segs: Seg[] = [
    { l: "Published", v: c.published - c.overdue, c: CH.pub },
    { l: "Review overdue", v: c.overdue, c: CH.over },
    { l: "In review", v: c.in_review, c: CH.rev },
    { l: "Changes requested", v: c.changes_requested, c: CH.chg },
    { l: "Drafts", v: c.drafts, c: CH.draft },
  ].filter((x) => x.v > 0 || x.l === "Published");
  const segTab: Record<string, string> = { Published: "pub", "Review overdue": "due", "In review": "rev", "Changes requested": "chg", Drafts: "draft" };

  const cos = ["CPY", ...p.myCodes.filter((x) => ws === "ALL" || x === ws)];
  // Documents by type: summed over the companies in view, biggest first, top 7 + Other.
  const byType: Record<string, number> = {};
  for (const co of cos) for (const [k, n] of Object.entries(data.by_company[co] ?? {})) byType[k] = (byType[k] ?? 0) + Number(n);
  const typeRows = Object.entries(byType).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1] || (TYPE_LABELS[a[0]] ?? a[0]).localeCompare(TYPE_LABELS[b[0]] ?? b[0]));
  const typeBars: { l: string; v: number; c: string; k?: string }[] = typeRows.slice(0, 7).map(([k, n]) => ({ l: TYPE_LABELS[k] ?? k, v: n, c: TYPE_COLORS[k] ?? "#8A93A0", k }));
  const otherRows = typeRows.slice(7);
  if (otherRows.length) typeBars.push({ l: `Other (${otherRows.length} types)`, v: otherRows.reduce((a, [, n]) => a + n, 0), c: "#A9AFB8" });
  const months = data.review_months.slice(0, 3).map((m, i) => ({
    l: new Date(`${m.month}-15T12:00:00`).toLocaleDateString(undefined, { month: "short" }),
    v: m.count,
    c: i === 0 ? "#DC6D27" : i === 1 ? "#E8A24A" : "#3B82C4",
  }));
  const cols = [{ l: "Overdue", v: c.overdue, c: CH.over, lc: "#AE2A20" }, ...months];
  const areas = data.by_area.slice(0, 6).map((a, i) => ({ l: a.label, v: a.count, c: AREA_COLORS[i]! }));
  const h = data.health;
  const within = h.within_review_pct / 100;
  const turnDays = h.avg_approval_days;

  const kc = (k: string, v: number, l: string, col: string, trend: number[], delta: string, dcls: string) => (
    <div className="kc" style={{ ["--kc" as string]: col }} onClick={() => libTab(k)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") libTab(k); }}>
      <span className="v">{v}</span>
      <span className="l">{l}</span>
      <div className="foot"><span className={`delta ${dcls}`}>{delta}</span><Spark vals={trend} color={col} /></div>
    </div>
  );

  return (
    <div className="view">
      <div className="hgroup">
        <div>
          <div className="eyebrow">{wsLabel} · {today}</div>
          <h1 className="title">{greet}, {first}</h1>
          <div className="lede summary">
            {c.awaiting_me ? <Link to="/approvals">{c.awaiting_me} waiting on your approval</Link> : "Nothing waiting on your approval"}
            {c.changes_requested > 0 && <> · <a onClick={() => libTab("chg")}>{c.changes_requested} need{c.changes_requested === 1 ? "s" : ""} changes</a></>}
            {c.overdue > 0 && <> · <a onClick={() => libTab("due")}>{c.overdue} past review date</a></>}
          </div>
        </div>
      </div>

      <div className="kpis">
        {kc("pub", c.published, "Published documents", "#2F9E5B", data.trend.approved,
          data.trend.approved_30d ? `▲ ${data.trend.approved_30d} approved in 30 days` : "No approvals in 30 days", data.trend.approved_30d ? "up" : "flat")}
        {kc("rev", c.in_review, "Awaiting approval", "#E0A31A", data.trend.submitted, c.in_review ? `${c.awaiting_me} on you` : "All clear", "flat")}
        {kc("chg", c.changes_requested, "Changes requested", "#D9486B", data.trend.changes, c.changes_requested ? "Back with authors" : "None open", c.changes_requested ? "dn" : "flat")}
        {kc("due", c.due_30 + c.overdue, "Review due or overdue", "#DC6D27", [c.overdue, ...data.review_months.map((m) => m.count)],
          c.overdue ? `${c.overdue} overdue` : "On schedule", c.overdue ? "dn" : "up")}
      </div>

      {c.total === 0 ? (
        <div className="card"><Empty title="Nothing here yet">
          {p.anyAuthor ? <>Start the library with <Link to="/new">New document</Link>. It publishes once an approver signs off.</> : "Documents appear here once they're approved."}
        </Empty></div>
      ) : (
        <>
          <div className="grid3">
            <div className="card panel">
              <h3>Library health <Link to="/library">Library →</Link></h3>
              <div className="cap">Every document you can see, by status</div>
              <div className="donutwrap">
                <Donut segs={segs} center={c.total} sub="documents" />
                <div className="legend">
                  {segs.map((x) => <div key={x.l} onClick={() => libTab(segTab[x.l]!)}><i style={{ background: x.c }} />{x.l}<b>{x.v}</b></div>)}
                </div>
              </div>
            </div>
            <div className="card panel">
              <h3>Documents by type</h3>
              <div className="cap">{ws === "ALL" ? "All your companies" : coName(ws)}, biggest first · click a bar to open it</div>
              <div style={{ marginTop: 10 }}>
                <HBars items={typeBars} labelW={150} onPick={(k) => nav(`/library?type=${k}`)} />
              </div>
            </div>
            <div className="card panel">
              <h3>Compliance pulse</h3>
              <div className="cap">How current the library is</div>
              <div className="gauges">
                <div><Gauge pct={within} label="Within review date" color={within >= 0.9 ? "#2F9E5B" : within >= 0.75 ? "#E0A31A" : "#D9486B"} valText={`${h.within_review_pct}%`} /><div className="gl">Within review date</div></div>
                <div><Gauge pct={turnDays === null ? 0 : Math.max(0.05, 1 - turnDays / 5)} label="Average approval time" color="#3B82C4" valText={turnDays === null ? "-" : `${turnDays}d`} /><div className="gl">Avg. approval time</div></div>
                <div><Gauge pct={h.two_person_pct / 100} label="Second-approver sign-off" color="#7C5CC4" valText={`${h.two_person_pct}%`} /><div className="gl">Second-approver sign-off</div></div>
                <div><Gauge pct={(h.opened_90d_pct ?? 0) / 100} label="Opened in the last 90 days" color="#1F9E8F" valText={h.opened_90d_pct === null ? "-" : `${h.opened_90d_pct}%`} /><div className="gl">Opened in last 90 days</div></div>
              </div>
            </div>
          </div>

          <div className="grid2d">
            <div className="card panel">
              <h3>Reviews coming due <a onClick={() => libTab("due")}>See all →</a></h3>
              <div className="cap">Overdue and the next 3 months</div>
              <Columns items={cols} />
            </div>
            <div className="card panel">
              <h3>Your queue {(p.anyApprover || p.anyAuthor) && <Link to={p.anyApprover ? "/approvals" : "/drafts"}>Open →</Link>}</h3>
              <div className="cap">Approvals and drafts that need you</div>
              {data.queue.length ? (
                <ul className="ql">
                  {data.queue.slice(0, 6).map((q) => (
                    <li key={q.id + q.kind} onClick={() => nav(`/doc/${q.id}`)}>
                      <TypeBadge type={q.type} sm />
                      <div className="t">
                        <b>{q.title}</b>
                        <span>
                          {q.kind === "ack" ? `Please read and acknowledge${q.by ? ` · approved by ${q.by}` : ""}` : q.kind === "approve" || q.kind === "self_approve" || q.kind === "review" ? `Submitted by ${q.by ?? "someone"} · ${fmtDT(q.at)}`
                            : q.kind === "fix" ? `Changes requested${q.comment ? ` · ${q.comment.slice(0, 60)}${q.comment.length > 60 ? "…" : ""}` : ""}`
                            : "Not yet submitted"}
                        </span>
                      </div>
                      {q.kind === "approve" && <span className="pill sm rev">Approve</span>}
                      {q.kind === "review" && <span className="pill sm info">Review</span>}
                      {q.kind === "ack" && <span className="pill sm pub">Acknowledge</span>}
                      {q.kind === "self_approve" && <span className="pill sm rev">Self-approve</span>}
                      {q.kind === "fix" && <span className="pill sm chg">Fix</span>}
                      {q.kind === "draft" && <span className="pill sm draft">Draft</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="empty" style={{ padding: "26px 10px" }}><h3 style={{ fontSize: 18 }}>All clear</h3>Nothing needs you right now.</div>
              )}
            </div>
          </div>

          <div className="card panel act-wide">
            <div>
              <h3>Recent activity</h3>
              <div className="cap">Approvals, submissions and change requests</div>
              {data.activity.length ? (
                <ul className="feed">
                  {data.activity.slice(0, 8).map((a, i) => {
                    const d = a.details ?? {};
                    const self = a.action === "version.approve" && d.self_approved === true;
                    const color = a.action === "version.approve" ? CH.pub : a.action === "version.changes_requested" ? CH.chg : a.action === "version.submit" ? CH.rev : CH.draft;
                    return (
                      <li key={i}>
                        <span className="dot" style={{ background: color }} />
                        <div>
                          <b>{a.actor_name ?? "Someone"}</b> {self ? "self-approved" : VERB[a.action] ?? a.action}{" "}
                          <a onClick={() => nav(`/doc/${a.document_id}`)} style={{ cursor: "pointer", color: "var(--ink)", fontWeight: 600 }}>{a.title}</a>
                          {a.action === "version.approve" && typeof d.version === "string" ? ` v${d.version}` : a.action === "version.approve" && typeof d.revision === "number" ? ` v${d.revision}.0.0` : ""}
                          <small>{fmtDT(a.at)}</small>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : <div className="cap">No activity yet.</div>}
            </div>
            {areas.length > 0 && (
              <div>
                <h3>Coverage by area</h3>
                <div className="cap" style={{ marginBottom: 10 }}>Documents per area, top 6</div>
                <HBars items={areas} labelW={150} />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
